import { useQueries } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 한국 공휴일 — <b>서버 표({@code ref_holidays})가 정한다</b> (사용자 지시 2026-08-27).
 *
 * <h2>손으로 관리하던 표를 버렸다</h2>
 * 여기에는 원래 연도별 날짜가 코드로 박혀 있었다(v1 {@code lib/monthlyMeetings.ts} 에서 옮겨 온 표).
 * 그 표는 <b>2026년치만</b> 채워져 있었고, 해가 바뀌면 아무도 다음 해를 넣지 않는다 — 2027년
 * 1월의 달력은 조용히 공휴일 없는 달이 되고, 그 사실은 연차를 세다가 알게 된다.
 *
 * <p>서버에는 이미 같은 표가 있고({@code ref_holidays}, V77) <b>매월 1일 새벽에 공공데이터포털에서
 * 올해·내년을 받아 둔다</b>({@code HolidayService#syncScheduled}). 임시공휴일도 그 길로 들어온다.
 * 화면이 자기 표를 따로 들고 있으면 둘은 반드시 어긋나고, 어긋난 쪽이 화면이다.
 *
 * <h2>읽기는 그대로 «동기» 다</h2>
 * 달력 화면 여덟 곳이 {@code getHolidayName(date)} 를 그리는 도중에 부른다. 그 자리를 전부
 * 비동기로 바꾸는 것은 이 변경이 할 일이 아니라서, <b>받아 둔 것을 모듈 캐시에 담고</b> 읽기는
 * 그대로 둔다. 캐시를 채우는 것은 {@link useHolidayTable} 이고, 앱 껍데기가 한 번 부른다
 * ({@code AppLayout}) — 화면마다 부르지 않아도 되게 하려는 것이다.
 *
 * <p>그래서 <b>첫 응답이 오기 전에는 공휴일이 없다</b>. 잠깐 평일처럼 보이는 편이, 낡은 표를
 * 들고 있다가 틀린 날을 빨갛게 칠하는 것보다 낫다 — 후자는 아무도 의심하지 않는다.
 */

/*
  에이전트도 <b>같은 서버 표</b>를 읽는다 (2026-09-18).

  한때 여기에 대조용 사본(`KOREAN_PUBLIC_HOLIDAYS`)이 서 있었다. 에이전트가 파일로 든 표
  (`agent/app/tools/_holidays.py`)와 글자로 맞춰 보려던 것인데, 위에 적힌 대로 화면은 그 상수를
  읽지 않으므로 <b>아무도 안 보는 칸을 사람이 손으로 채우는</b> 일만 남았다. 사본과 대조 시험을
  함께 걷어내고 에이전트가 `GET /v1/holidays` 를 부르게 했다 — 정본이 하나면 어긋날 자리가 없다.
*/

type HolidayYear = {
  year: number;
  holidays: Array<{ date: string; name: string }>;
};

/** `YYYY-MM-DD` → 이름. 서버가 준 것만 들어간다. */
const table = new Map<string, string>();

/** `YYYY-MM-DD`. 로컬 시각 기준이라 `toISOString()` 과 달리 하루가 밀리지 않는다. */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getHolidayName(date: Date): string | null {
  return table.get(formatDateKey(date)) ?? null;
}

/** 아무도 해를 말하지 않을 때 받아 둘 해들. 오늘 언저리를 보는 화면이 대부분이다. */
function nearbyYears(): number[] {
  const year = new Date().getFullYear();
  return [year - 1, year, year + 1];
}

/**
 * 두 날짜에 걸친 해들. 달력 격자가 이웃 달을 함께 그려 1월·12월에서는 해가 걸친다.
 *
 * <p>{@link useHolidayTable} 에 그대로 넘긴다 — 「지금 그리는 칸들이 속한 해」 가 곧 받아올 해다.
 */
export function yearsOf(start: Date, end: Date): number[] {
  const years: number[] = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year++) {
    years.push(year);
  }
  return years;
}

/**
 * 공휴일 표를 받아 캐시에 담는다. <b>앱 껍데기에서 한 번</b> 부른다({@code AppLayout}).
 *
 * <p>반환값은 「몇 해가 도착했나」 뿐이다. 값을 쓰는 것은 {@link getHolidayName} 이고, 이 훅이
 * 반환값을 바꾸면서 <b>부른 컴포넌트를 다시 그리게</b> 하는 것이 진짜 역할이다 — 그래야 캐시가
 * 채워진 순간 그 아래 달력들이 공휴일과 함께 다시 그려진다.
 *
 * <p>하루를 그대로 들고 있는다({@code staleTime}). 공휴일은 화면을 보는 동안 바뀌지 않는다.
 *
 * <h2>보고 있는 해를 넘길 수 있다 (사용자 지시 2026-08-27)</h2>
 * 껍데기가 받아 두는 것은 <b>올해 언저리</b>뿐이다. 달력을 2029년으로 넘긴 사람에게 그 해는
 * 공휴일이 하나도 없는 해로 보이는데, 표가 비어서가 아니라 <b>아무도 그 해를 묻지 않아서</b>다
 * (서버는 물으면 그 자리에서 받아 온다 — {@code HolidayService#forYear}).
 *
 * <p>그래서 해를 옮겨 다니는 화면은 <b>자기가 그리고 있는 해</b>를 넘긴다. 같은 해를 여러 화면이
 * 물어도 요청은 한 번이다(질의 열쇠가 같다).
 *
 * @param years 받아 둘 해들. 안 주면 올해 ±1년
 */
export function useHolidayTable(years: number[] = nearbyYears()): number {
  const results = useQueries({
    queries: years.map((year) => ({
      queryKey: ['holidays', year],
      queryFn: () => apiFetch<HolidayYear>(`/v1/holidays?year=${year}`),
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
    })),
  });

  let loaded = 0;
  for (const result of results) {
    if (!result.data) continue;
    loaded++;
    for (const holiday of result.data.holidays) {
      table.set(holiday.date, holiday.name);
    }
  }
  return loaded;
}
